/**
 * PortfolioReferencesStep
 * Contractor onboarding step — Portfolio projects only (references removed).
 *
 * The contractor adds completed projects via a modal ("Add portfolio"). Each
 * project records the work address (Google-autocomplete, with manual fallback),
 * at least one photo, and a CONTACTABLE CLIENT (name + phone are mandatory) —
 * that client doubles as the project reference we will phone and visit. There is
 * no separate references list any more.
 *
 *   Portfolio → POST /contractors/portfolio (writes `contractor_portfolio`, the
 *               table the application checklist reads). Photos upload via
 *               POST /uploads/portfolio (max 5/request, so we batch).
 *
 * Each project is persisted on Save; counts are reported up via
 * onChange({ portfolioCount, referencesCount }) so the wizard can gate Next.
 */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box, Typography, Button, TextField, Grid, Paper,
  IconButton, Alert, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Add, Delete, CloudUpload, CheckCircle, Close, ChevronLeft, ChevronRight, PhotoLibrary } from '@mui/icons-material';
import api from '../../../services/api';
import contractorVerificationService from '../../../services/contractorVerificationService';
import LocationInput from '../../common/LocationInput';

const MIN_PORTFOLIO_PHOTOS = 1;
const UPLOAD_BATCH = 5; // POST /uploads/portfolio accepts max 5 images per request

const emptyProject = {
  title: '', completionDate: '',
  clientName: '', clientPhone: '', clientEmail: '',
};

/** Coerce a jsonb/text[] image column that may arrive as an array or JSON string. */
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
  }
  return [];
};

/**
 * Photo carousel — controlled index so prev/next reliably advance. Used in the
 * project detail modal.
 */
function Carousel({ photos = [], height = 300 }) {
  const n = photos.length;
  const [idx, setIdx] = useState(0);
  const safe = n ? ((idx % n) + n) % n : 0;
  const arrowSx = (side) => ({
    position: 'absolute', [side]: 8, top: '50%', transform: 'translateY(-50%)',
    bgcolor: 'rgba(255,255,255,0.92)', boxShadow: 2, zIndex: 2, '&:hover': { bgcolor: '#fff' },
  });
  return (
    <Box sx={{ position: 'relative', height, bgcolor: '#0d1117', borderRadius: 1, overflow: 'hidden' }}>
      {n > 0 ? (
        <Box component="img" src={photos[safe]} alt={`Photo ${safe + 1}`}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>No photos</Typography>
        </Box>
      )}
      {n > 1 && (
        <>
          <IconButton onClick={() => setIdx((i) => (i - 1 + n) % n)} aria-label="Previous photo" sx={arrowSx('left')}>
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={() => setIdx((i) => (i + 1) % n)} aria-label="Next photo" sx={arrowSx('right')}>
            <ChevronRight />
          </IconButton>
        </>
      )}
      {n > 0 && (
        <Box sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.65)', color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem', zIndex: 2 }}>
          {safe + 1} / {n}
        </Box>
      )}
    </Box>
  );
}

/**
 * One saved project as a clickable cover card. Clicking opens the detail/edit
 * modal (where the photos can be flipped through and details edited).
 */
function ProjectCard({ project, color, onOpen, onDelete, deleting }) {
  const photos = project.photos || [];
  const cover = photos[0];
  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        overflow: 'hidden', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column',
        cursor: 'pointer', transition: 'box-shadow .2s, transform .2s',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
      }}
    >
      <Box sx={{ position: 'relative', height: 170, bgcolor: '#f2f4f5' }}>
        {cover ? (
          <Box component="img" src={cover} alt={project.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.disabled">No photo</Typography>
          </Box>
        )}
        {photos.length > 0 && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem' }}>
            <PhotoLibrary sx={{ fontSize: 14 }} /> {photos.length}
          </Box>
        )}
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} disabled={deleting} aria-label="Remove project"
          sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: 1, '&:hover': { bgcolor: '#fff' } }}>
          {deleting ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <CheckCircle sx={{ color, fontSize: 16, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} noWrap>{project.title || 'Untitled project'}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
          Client: {project.clientName || '—'}
        </Typography>
        {project.location && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            📍 {project.location}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color, fontWeight: 600 }}>View / edit</Typography>
      </Box>
    </Paper>
  );
}

/** Upload an array of File objects to /uploads/portfolio in batches; returns all URLs. */
async function uploadPhotos(files) {
  const urls = [];
  for (let i = 0; i < files.length; i += UPLOAD_BATCH) {
    const batch = files.slice(i, i + UPLOAD_BATCH);
    const fd = new FormData();
    batch.forEach((f) => fd.append('images', f));
    const res = await api.post('/uploads/portfolio', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const batchUrls = res.data?.data?.urls || [];
    urls.push(...batchUrls);
  }
  return urls;
}

const PortfolioReferencesStep = forwardRef(({ onChange, color = '#43B581' }, forwardedRef) => {
  const [projects, setProjects] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [proj, setProj] = useState(emptyProject);
  const [projLocation, setProjLocation] = useState(null); // structured address from LocationInput
  const [projFiles, setProjFiles] = useState([]);
  const [savingProj, setSavingProj] = useState(false);
  const [projError, setProjError] = useState('');

  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Detail / edit modal
  const [detail, setDetail] = useState(null);     // the project being viewed
  const [editForm, setEditForm] = useState(null); // editable fields
  const [editLocation, setEditLocation] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const report = useCallback((p) => {
    // referencesCount kept in the payload shape for the wizard, but references are
    // gone — clients on each project are the references now.
    if (onChange) onChange({ portfolioCount: p.length, referencesCount: p.length });
  }, [onChange]);

  // Reload projects from the server (presigned image URLs) so a returning
  // applicant SEES their portfolio with thumbnails, and so a just-added project
  // shows the same signed photos. Avoids a blank list that breeds duplicates.
  const loadProjects = useCallback(async () => {
    try {
      const res = await api.get('/contractors/portfolio');
      const rows = res.data?.data || [];
      const mapped = rows.map((r) => {
        const photos = [...asArray(r.after_images), ...asArray(r.before_images)];
        return {
          id: r.id,
          title: r.project_title || '',
          location: r.location || '',
          clientName: r.client_name || '',
          clientPhone: r.client_phone || '',
          clientEmail: r.client_email || '',
          photos,
          photoCount: photos.length,
          existing: true,
        };
      });
      setProjects(mapped);
      report(mapped);
      return mapped;
    } catch (_) {
      return null; // no projects yet, or fetch failed — keep current list
    }
  }, [report]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadProjects();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loadProjects]);

  const setProjField = (k, v) => setProj((s) => ({ ...s, [k]: v }));

  const handleDeleteProject = async (idx) => {
    const target = projects[idx];
    if (target?.id) {
      setDeletingId(target.id);
      try {
        await api.delete(`/contractors/portfolio/${target.id}`);
      } catch (err) {
        setProjError('Could not remove that project. Please try again.');
        setDeletingId(null);
        return;
      }
      setDeletingId(null);
    }
    const next = projects.filter((_, i) => i !== idx);
    setProjects(next);
    report(next);
  };

  const openModal = () => {
    setProjError('');
    setProj(emptyProject);
    setProjLocation(null);
    setProjFiles([]);
    setModalOpen(true);
  };
  const closeModal = () => { if (!savingProj) setModalOpen(false); };

  const openDetail = (p) => {
    setEditError('');
    setDetail(p);
    setEditForm({
      title: p.title || '',
      clientName: p.clientName || '',
      clientPhone: p.clientPhone || '',
      clientEmail: p.clientEmail || '',
    });
    setEditLocation(p.location ? { address: p.location } : null);
  };
  const closeDetail = () => { if (!savingEdit) { setDetail(null); setEditForm(null); } };
  const setEditField = (k, v) => setEditForm((s) => ({ ...s, [k]: v }));

  const handleSaveEdit = async () => {
    if (!editForm) return;
    setEditError('');
    if (!editForm.title.trim()) { setEditError('Add a project title.'); return; }
    if (!editForm.clientName.trim()) { setEditError('Add the client name.'); return; }
    if (!editForm.clientPhone.trim()) { setEditError('Add the client phone number.'); return; }
    const newLocation = (editLocation?.address || editLocation?.formattedAddress || '').trim();
    setSavingEdit(true);
    try {
      await api.put(`/contractors/portfolio/${detail.id}`, {
        project_title: editForm.title.trim(),
        client_name: editForm.clientName.trim(),
        client_phone: editForm.clientPhone.trim(),
        client_email: editForm.clientEmail.trim() || null,
        location: newLocation || null,
      });
      await loadProjects();
      setDetail(null);
      setEditForm(null);
    } catch (err) {
      setEditError(err?.response?.data?.message || 'Could not save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handlePhotosSelected = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (picked.length) setProjFiles((prev) => [...prev, ...picked]);
    e.target.value = ''; // allow re-selecting the same file
  };
  const removePhoto = (idx) => setProjFiles((prev) => prev.filter((_, i) => i !== idx));

  // Mandatory to save a project: title, a contactable client (name + phone), and
  // at least one photo. Address is captured via the lookup but never blocks (an
  // address Google can't find can still be typed and saved).
  const locationText = (projLocation?.address || projLocation?.formattedAddress || '').trim();
  const canAddProject =
    proj.title.trim() &&
    proj.clientName.trim() &&
    proj.clientPhone.trim() &&
    projFiles.length >= MIN_PORTFOLIO_PHOTOS &&
    !savingProj;

  const handleAddProject = async () => {
    setProjError('');
    if (!proj.title.trim()) { setProjError('Add a project title.'); return null; }
    if (!proj.clientName.trim()) { setProjError('Add the client name — we contact them as your reference.'); return null; }
    if (!proj.clientPhone.trim()) { setProjError('Add the client phone number — we phone and visit them to verify this project.'); return null; }
    if (projFiles.length < MIN_PORTFOLIO_PHOTOS) {
      setProjError(`Add at least ${MIN_PORTFOLIO_PHOTOS} photo.`);
      return null;
    }
    setSavingProj(true);
    try {
      const urls = await uploadPhotos(projFiles);
      if (urls.length < MIN_PORTFOLIO_PHOTOS) {
        setProjError('Photos failed to upload. Please try again.');
        setSavingProj(false);
        return null;
      }
      const completionIso = proj.completionDate ? new Date(proj.completionDate).toISOString() : null;
      await api.post('/contractors/portfolio', {
        project_title: proj.title.trim(),
        location: locationText || null,
        completion_date: completionIso,
        client_name: proj.clientName.trim() || null,
        client_phone: proj.clientPhone.trim() || null,
        client_email: proj.clientEmail.trim() || null,
        after_images: urls,
        is_featured: true,
      });

      // Mirror to verification evidence so verifiers can see this project.
      // Non-fatal: a hiccup here must not block the contractor from proceeding.
      try {
        await contractorVerificationService.addPortfolioEvidence({
          projectTitle: proj.title.trim(),
          projectLocation: locationText || null,
          completedAt: completionIso,
          clientName: proj.clientName.trim() || null,
          clientPhone: proj.clientPhone.trim() || null,
          clientEmail: proj.clientEmail.trim() || null,
          evidenceUrls: urls,
        });
      } catch (evidenceErr) {
        // eslint-disable-next-line no-console
        console.warn('[PortfolioReferencesStep] verification evidence mirror failed (non-fatal):', evidenceErr?.response?.data?.message || evidenceErr?.message);
      }

      // Refetch so the new project appears with presigned thumbnails, consistent
      // with the rest of the list.
      const next = await loadProjects();
      setModalOpen(false);
      return next || projects;
    } catch (err) {
      setProjError(err?.response?.data?.message || 'Could not save this project. Please try again.');
      return null;
    } finally {
      setSavingProj(false);
    }
  };

  // The wizard calls flushPending() on Next. With the modal flow there is no
  // inline half-filled entry to commit, so we just report current counts.
  useImperativeHandle(forwardedRef, () => ({
    async flushPending() {
      report(projects);
      return { portfolioCount: projects.length, referencesCount: projects.length };
    },
  }), [projects, report]);

  return (
    <Box>
      <Box textAlign="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Portfolio</Typography>
        <Typography variant="body2" color="text.secondary">
          Add completed projects (with at least {MIN_PORTFOLIO_PHOTOS} photo each). This is what verifiers and clients review.
        </Typography>
      </Box>

      {/* Bold notice — the client on each project is a reference we verify. */}
      <Alert severity="info" icon={false} sx={{ mb: 2, border: `1px solid ${color}`, bgcolor: '#E8F8F0' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a5c3a' }}>
          We contact and visit the clients you list here.
        </Typography>
        <Typography variant="body2" sx={{ color: '#1a5c3a' }}>
          Each project must name a client with a working phone number — we phone and
          visit them to verify your work. Make sure their details are correct.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Portfolio projects</Typography>
          <Chip
            size="small"
            label={`${projects.length} added`}
            color={projects.length >= 1 ? 'success' : 'default'}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={18} sx={{ color }} />
            <Typography variant="body2" color="text.secondary">Loading your saved projects…</Typography>
          </Box>
        )}

        {!loading && projects.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {projects.map((p, i) => (
              <Grid item xs={12} sm={6} key={p.id || i}>
                <ProjectCard
                  project={p}
                  color={color}
                  deleting={deletingId === p.id}
                  onOpen={() => openDetail(p)}
                  onDelete={() => handleDeleteProject(i)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openModal}
          sx={{ bgcolor: color, '&:hover': { bgcolor: color } }}
        >
          Add portfolio
        </Button>
        {!loading && projects.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Add at least one completed project to continue.
          </Typography>
        )}
      </Paper>

      {/* ── ADD-PROJECT MODAL ─────────────────────────────────────────── */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Add portfolio project
          <IconButton onClick={closeModal} disabled={savingProj} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {projError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProjError('')}>{projError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Project title" value={proj.title}
                onChange={(e) => setProjField('title', e.target.value)}
                placeholder="e.g. 4-bedroom house, Borrowdale" />
            </Grid>

            {/* Address — same Google-autocomplete the Basic Info / New Project flows use.
                If Google can't find it, the manual-entry tab still lets them save it. */}
            <Grid item xs={12}>
              <LocationInput
                label="Address of the project"
                value={projLocation}
                onChange={setProjLocation}
                helperText="Where the work was done. If it isn't found, switch to Manual Entry and type it — you can still save."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Completed date" InputLabelProps={{ shrink: true }}
                value={proj.completionDate} onChange={(e) => setProjField('completionDate', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Client name" value={proj.clientName}
                onChange={(e) => setProjField('clientName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Client phone" value={proj.clientPhone}
                onChange={(e) => setProjField('clientPhone', e.target.value)}
                placeholder="+263 7X XXX XXXX" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client email (optional)" type="email" value={proj.clientEmail}
                onChange={(e) => setProjField('clientEmail', e.target.value)} />
            </Grid>

            <Grid item xs={12}>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                Add photos
                <input type="file" accept="image/*" hidden multiple onChange={handlePhotosSelected} />
              </Button>
              <Typography variant="caption" sx={{ ml: 1.5 }}
                color={projFiles.length >= MIN_PORTFOLIO_PHOTOS ? 'success.main' : 'text.secondary'}>
                At least {MIN_PORTFOLIO_PHOTOS} photo — add as many as you like ({projFiles.length} added)
              </Typography>
              {projFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {projFiles.map((f, i) => (
                    <Chip key={i} label={f.name} size="small"
                      onDelete={() => removePhoto(i)} deleteIcon={<Delete />} />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeModal} disabled={savingProj} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            startIcon={savingProj ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            disabled={!canAddProject}
            onClick={handleAddProject}
            sx={{ bgcolor: color, '&:hover': { bgcolor: color } }}
          >
            {savingProj ? 'Saving…' : 'Save project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PROJECT DETAIL / EDIT MODAL ───────────────────────────────── */}
      <Dialog open={!!detail} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Project details
          <IconButton onClick={closeDetail} disabled={savingEdit} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail && editForm && (
            <>
              <Carousel photos={detail.photos || []} />

              {editError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setEditError('')}>{editError}</Alert>}

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField fullWidth required label="Project title" value={editForm.title}
                    onChange={(e) => setEditField('title', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <LocationInput
                    label="Address of the project"
                    value={editLocation}
                    onChange={setEditLocation}
                    helperText="If it isn't found on the map, switch to Manual Entry and type it."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth required label="Client name" value={editForm.clientName}
                    onChange={(e) => setEditField('clientName', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth required label="Client phone" value={editForm.clientPhone}
                    onChange={(e) => setEditField('clientPhone', e.target.value)}
                    placeholder="+263 7X XXX XXXX" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Client email (optional)" type="email" value={editForm.clientEmail}
                    onChange={(e) => setEditField('clientEmail', e.target.value)} />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDetail} disabled={savingEdit} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            startIcon={savingEdit ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            disabled={savingEdit}
            onClick={handleSaveEdit}
            sx={{ bgcolor: color, '&:hover': { bgcolor: color } }}
          >
            {savingEdit ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

PortfolioReferencesStep.displayName = 'PortfolioReferencesStep';

export default PortfolioReferencesStep;
